import { useEffect } from "react";
import type { BlogPost } from "@/mocks/blogPosts";

const categoryColors: Record<string, string> = {
  "Materials & Standards": "bg-teal-100 text-teal-700",
  "HVAC Systems": "bg-sky-100 text-sky-700",
  "Case Studies": "bg-amber-100 text-amber-700",
  "Compliance & Regulations": "bg-rose-100 text-rose-700",
  "Industry Trends": "bg-violet-100 text-violet-700",
  "How-To Guides": "bg-emerald-100 text-emerald-700",
};

interface Props {
  post: BlogPost;
  onClose: () => void;
}

export default function BlogPostModal({ post, onClose }: Props) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  const paragraphs = post.content.trim().split("\n\n").filter(Boolean);

  const renderParagraph = (text: string, idx: number) => {
    if (text.startsWith("**") && text.endsWith("**")) {
      return (
        <h3 key={idx} className="text-lg font-bold text-gray-900 mt-6 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          {text.replace(/\*\*/g, "")}
        </h3>
      );
    }
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={idx} className="text-gray-600 text-[15px] leading-relaxed mb-4">
        {parts.map((part, i) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={i} className="font-semibold text-gray-900">{part.replace(/\*\*/g, "")}</strong>;
          }
          return part;
        })}
      </p>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header image */}
        <div className="h-[260px] overflow-hidden rounded-t-lg relative">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${categoryColors[post.category] || "bg-gray-100 text-gray-600"}`}>
              {post.category}
            </span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <i className="ri-time-line"></i> {post.readTime}
            </span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <i className="ri-calendar-line"></i> {post.date}
            </span>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
            {post.title}
          </h2>
          <p className="text-sm text-gray-500 mb-6 flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center bg-teal-50 rounded-full">
              <i className="ri-user-line text-xs text-teal-600"></i>
            </span>
            {post.author}
          </p>

          <div className="prose max-w-none">
            {paragraphs.map((p, i) => renderParagraph(p, i))}
          </div>

          <div className="mt-8 pt-6 border-t border-gray-100 flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex gap-3">
              <a href="https://isman.co.ke" target="_blank" rel="noopener noreferrer nofollow" className="text-xs text-gray-500 hover:text-teal-600 flex items-center gap-1 cursor-pointer whitespace-nowrap">
                <i className="ri-share-line"></i> Share
              </a>
            </div>
            <button
              onClick={onClose}
              className="bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold px-5 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Close Article
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
